// Resizing a box by one of its corners.
//
// The rule that makes this feel right is that the OPPOSITE corner stays
// where it is. Without it a box can only ever grow right and down, which
// is why a single bottom-right handle was not enough: a block whose top
// edge is in the wrong place can only be fixed from the top edge.
//
// Shared by the three things that have corner handles — colour blocks,
// images and form fields — because they had begun to grow three copies of
// the same arithmetic, and an off-by-one in any of them would show up as
// a box that jumps when you grab it.

export type Corner = "nw" | "ne" | "sw" | "se";

/** x0, y0, x1, y1 — always with x0 < x1 and y0 < y1. */
export type Box = [number, number, number, number];

/** Which edges a corner drags: -1 = the low edge (so the ORIGIN moves),
 *  +1 = the high edge. */
export function cornerSigns(corner: Corner): { sx: -1 | 1; sy: -1 | 1 } {
  return {
    sx: corner === "nw" || corner === "sw" ? -1 : 1,
    sy: corner === "nw" || corner === "ne" ? -1 : 1,
  };
}

/** The box a free (unconstrained) corner drag describes.
 *
 *  `min` keeps a box from being dragged inside out or down to nothing —
 *  a zero-width block is invisible and unselectable, so the drag stops
 *  rather than producing one. */
export function resizeFree(box: Box, corner: Corner, dx: number, dy: number, min = 4): Box {
  const [x0, y0, x1, y1] = box;
  const { sx, sy } = cornerSigns(corner);
  return [
    sx < 0 ? Math.min(x0 + dx, x1 - min) : x0,
    sy < 0 ? Math.min(y0 + dy, y1 - min) : y0,
    sx < 0 ? x1 : Math.max(x1 + dx, x0 + min),
    sy < 0 ? y1 : Math.max(y1 + dy, y0 + min),
  ];
}

/** The box an aspect-LOCKED corner drag describes — for images, where
 *  squashing a photo is almost never what someone meant.
 *
 *  The horizontal component drives the size and the height follows, so
 *  the pointer leads the drag in the direction people push hardest; a
 *  west or north corner then places the box so its opposite corner is
 *  still pinned. */
export function resizeLocked(box: Box, corner: Corner, dx: number, min = 12): Box {
  const [x0, y0, x1, y1] = box;
  const w = x1 - x0;
  const h = y1 - y0;
  const { sx, sy } = cornerSigns(corner);
  const nw = Math.max(min, w + dx * sx);
  const nh = w === 0 ? h : (nw * h) / w;
  const nx = sx < 0 ? x1 - nw : x0;
  const ny = sy < 0 ? y1 - nh : y0;
  return [nx, ny, nx + nw, ny + nh];
}

/** Did a drag actually change the box? Sub-pixel drift from a click that
 *  merely brushed a handle should not become an undo step. */
export function boxChanged(a: Box, b: Box, tolerance = 1): boolean {
  return a.some((v, i) => Math.abs(v - b[i]!) > tolerance);
}
